import { Link, Routes, Route, Navigate } from "react-router-dom";
import routes from "routes";
import FixedPlugin from "components/fixedPlugin/FixedPlugin";
import { useTranslation } from "react-i18next";
import { RoutesType } from "types/globalTypes";
import SignUp from "views/auth/SignUp";
import ResetPassword from "views/auth/ResetPassword";
import SignIn from "views/auth/SignIn";
import LanguageSwitcher from "components/switcher/LanguageSwitcher";
import ResponderLogin from "views/auth/ResponderLogin";
import { motion, AnimatePresence } from "framer-motion";
import OrganizationLogin from "views/auth/OrganizationLogin";
import {
  HiOutlineShieldCheck,
  HiOutlineChartBar,
  HiOutlineUsers,
} from "react-icons/hi2";
import { useAppSelector } from "reduxFiles/hooks";
import VitalLogoSvg from "assets/img/logo/VitalLogo";
import Footer from "components/footer/Footer";
import AuthSwitcher from "components/switcher/AuthSwitcher";

export default function Auth() {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);

  const getRoutes = (routes: RoutesType[]): any => {
    return routes.map((prop, key) => {
      if (prop.layout === "/auth") {
        return (
          <Route path={`/${prop.path}`} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900">
      <FixedPlugin />

      <div className="flex h-full w-full">
        {/* Left Side - Brand Section */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative hidden h-full lg:block lg:w-1/2 xl:w-[55%] 2xl:w-[50%]"
        >
          <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-brand-600 via-indigo-600 to-brand-700 p-12 dark:from-brand-800 dark:via-indigo-800 dark:to-brand-900">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute left-1/4 top-1/4 h-64 w-64 rounded-full bg-white blur-3xl" />
              <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-white blur-3xl" />
              <div className="absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20" />
              <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10" />

              {/* Floating Shapes with brand colors */}
              <motion.div
                animate={{
                  y: [0, -20, 0],
                  rotate: [0, 5, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute left-10 top-20 text-4xl opacity-30"
              >
                ✦
              </motion.div>
              <motion.div
                animate={{
                  y: [0, 20, 0],
                  rotate: [0, -5, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute bottom-20 right-10 text-3xl opacity-30"
              >
                ✧
              </motion.div>
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute left-1/3 top-1/3 text-2xl opacity-20"
              >
                ●
              </motion.div>

              {/* Decorative grid dots */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                  backgroundSize: "40px 40px",
                }}
              />
            </div>

            {/* Main Content */}
            <div className="relative z-10 flex max-w-lg flex-col items-center justify-center text-center text-white">
              {/* Logo/Icon with brand colors */}
              <div className="logo-svg-container h-32 w-32">
                <VitalLogoSvg />
              </div>

              <div
                className="vital-text my-4 text-center text-white"
                style={{ fontFamily: "'Great Vibes', cursive" }}
              >
                Vital<sup className="vital-sup">+</sup>
              </div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mb-4 text-2xl font-bold tracking-tight"
              >
                {t("auth.welcomeTitle")}
              </motion.h1>

              {/* Features with brand colors */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3"
              >
                {[
                  {
                    icon: HiOutlineShieldCheck,
                    title: t("auth.feature1"),
                    desc: t("auth.feature1Desc"),
                  },
                  {
                    icon: HiOutlineChartBar,
                    title: t("auth.feature2"),
                    desc: t("auth.feature2Desc"),
                  },
                  {
                    icon: HiOutlineUsers,
                    title: t("auth.feature3"),
                    desc: t("auth.feature3Desc"),
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                    className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm transition-all duration-200 hover:bg-white/20"
                  >
                    <feature.icon className="mx-auto mb-2 h-8 w-8 text-indigo-200" />
                    <h3 className="text-md font-semibold">{feature.title}</h3>
                    <p className="text-sm text-white/70">{feature.desc}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-md mt-8 text-white/80"
              >
                {t("auth.welcomeDescription")}
              </motion.p>

              {/* Powered by with brand colors */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-8 flex items-center gap-2 text-sm text-white/60"
              >
                <HiOutlineSparkles className="h-4 w-4 text-indigo-300" />
                <span>{t("auth.poweredBy")}</span>
              </motion.div> */}
            </div>
          </div>
        </motion.div>

        {/* Right Side - Form */}
        <div className="flex h-full w-full flex-col lg:w-1/2 xl:w-[45%] 2xl:w-[50%]">
          <main className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-1 flex-col justify-center px-4 py-4 sm:px-6 lg:px-8">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="justify- mb-4 flex flex-shrink-0 flex-wrap items-center gap-7"
              >
                {user && (
                  <Link to="/" className="group flex items-center gap-2">
                    <motion.div
                      whileHover={{ x: -4 }}
                      transition={{ type: "spring", stiffness: 400 }}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-600 transition-all group-hover:bg-brand-200 dark:bg-brand-900/50 dark:text-brand-400"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 8 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="rtl:rotate-180"
                      >
                        <path
                          d="M6.70994 2.11997L2.82994 5.99997L6.70994 9.87997C7.09994 10.27 7.09994 10.9 6.70994 11.29C6.31994 11.68 5.68994 11.68 5.29994 11.29L0.709941 6.69997C0.319941 6.30997 0.319941 5.67997 0.709941 5.28997L5.29994 0.699971C5.68994 0.309971 6.31994 0.309971 6.70994 0.699971C7.08994 1.08997 7.09994 1.72997 6.70994 2.11997V2.11997Z"
                          fill="currentColor"
                        />
                      </svg>
                    </motion.div>
                    <span className="text-xs font-medium text-gray-600 transition-colors group-hover:text-brand-600 dark:text-gray-300 dark:group-hover:text-brand-400 sm:text-sm">
                      {t("buttons.backToHome")}
                    </span>
                  </Link>
                )}

                {/* lang switcher */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <LanguageSwitcher />
                </motion.div>
              </motion.div>

              {/* Auth Forms Container */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="flex min-h-0 flex-1 items-center justify-center"
              >
                <div className="mx-auto w-full max-w-sm sm:max-w-md lg:max-w-lg">
                  <div className="rounded-2xl bg-white/80 p-5 shadow-xl shadow-brand-100/50 backdrop-blur-sm dark:bg-navy-800/80 dark:shadow-navy-900/50 sm:p-7 lg:p-8">
                    {/* login switcher */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className="mb-6"
                    >
                      <AuthSwitcher />
                    </motion.div>

                    <AnimatePresence mode="wait">
                      <Routes>
                        {getRoutes(routes)}
                        <Route path="/sign-in" element={<SignIn />} />
                        <Route path="/sign-up" element={<SignUp />} />
                        <Route
                          path="/reset-password"
                          element={<ResetPassword />}
                        />
                        <Route
                          path="/organization-login"
                          element={<OrganizationLogin />}
                        />
                        <Route
                          path="/responder-login"
                          element={<ResponderLogin />}
                        />
                        <Route
                          path="/"
                          element={<Navigate to="/auth/sign-in" replace />}
                        />
                      </Routes>
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            </div>
          </main>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex-shrink-0"
          >
            <Footer />
          </motion.div>
        </div>
      </div>

      {/* Floating Animation Elements with brand colors */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <motion.div
          animate={{
            y: [0, -15, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -left-20 -top-20 h-56 w-56 rounded-full bg-brand-200/20 blur-3xl dark:bg-brand-500/10"
        />
        <motion.div
          animate={{
            y: [0, 15, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-indigo-200/20 blur-3xl dark:bg-indigo-500/10"
        />
      </div>
    </div>
  );
}
